import type { BuildingStorageQuota } from "@/domains/documents/services/buildingStorageQuota";

export interface UsageDocument {
  file_path: string | null;
  size: number | null;
  project_id: number | null;
}
export interface UsageProject {
  id: number;
  name: string;
}
export interface ProjectUsage extends UsageProject {
  usedBytes: number;
}
export interface BuildingUsage {
  id: number;
  name: string;
  quota: BuildingStorageQuota;
  projects: ProjectUsage[];
  otherBytes: number;
  overlapBytes: number;
}

export function storageBytes(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const bytes = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isSafeInteger(bytes) && bytes >= 0 ? bytes : null;
}

export function usagePercentage(bytes: number, denominator: number | null): number {
  return denominator !== null && denominator > 0 ? (bytes / denominator) * 100 : 0;
}

export function formatUsageBytes(bytes: number, locale: string): string {
  const [divisor, unit] =
    bytes >= 1_000_000_000
      ? ([1_000_000_000, "GB"] as const)
      : bytes >= 1_000_000
        ? ([1_000_000, "MB"] as const)
        : bytes >= 1_000
          ? ([1_000, "kB"] as const)
          : ([1, "B"] as const);
  const amount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: unit === "GB" ? 1 : 0,
    maximumFractionDigits: 1,
  }).format(bytes / divisor);
  return `${amount} ${unit}`;
}

interface OriginalPath {
  bytes: number;
  projects: Set<number>;
}

function originalPaths(documents: UsageDocument[]): Map<string, OriginalPath> {
  const paths = new Map<string, OriginalPath>();
  for (const document of documents) {
    if (document.file_path === null) continue;
    const bytes = storageBytes(document.size) ?? 0;
    const path = paths.get(document.file_path) ?? { bytes: 0, projects: new Set<number>() };
    path.bytes = Math.max(path.bytes, bytes);
    if (document.project_id !== null) path.projects.add(document.project_id);
    paths.set(document.file_path, path);
  }
  return paths;
}

function projectFootprints(
  paths: ReadonlyMap<string, OriginalPath>,
  projects: UsageProject[],
  metadata: ReadonlyMap<string, number>
) {
  const totals = new Map(projects.map((project) => [project.id, 0]));
  let countedBytes = 0,
    visibleBytes = 0,
    overlapBytes = 0;
  for (const [name, path] of paths) {
    const bytes = metadata.get(name) ?? path.bytes;
    countedBytes += bytes;
    const visible = [...path.projects].filter((id) => totals.has(id));
    if (visible.length > 0) visibleBytes += bytes;
    overlapBytes += Math.max(0, visible.length - 1) * bytes;
    for (const id of visible) totals.set(id, (totals.get(id) ?? 0) + bytes);
  }
  return { totals, countedBytes, visibleBytes, overlapBytes };
}

function sortedProjectUsage(projects: UsageProject[], totals: ReadonlyMap<number, number>) {
  return projects
    .map((project) => ({ ...project, usedBytes: totals.get(project.id) ?? 0 }))
    .sort((a, b) => b.usedBytes - a.usedBytes || a.id - b.id);
}

/** Project reference footprints may overlap; the building counts each original once. */
export function accountProjectStorage(
  documents: UsageDocument[],
  projects: UsageProject[],
  metadata: ReadonlyMap<string, number>,
  usedBytes: number
): Pick<BuildingUsage, "projects" | "otherBytes" | "overlapBytes"> {
  const { totals, countedBytes, visibleBytes, overlapBytes } = projectFootprints(
    originalPaths(documents),
    projects,
    metadata
  );
  if (!Number.isSafeInteger(countedBytes) || countedBytes > usedBytes) {
    throw new Error("Storage changed while loading. Please refresh.");
  }
  return {
    projects: sortedProjectUsage(projects, totals),
    otherBytes: usedBytes - visibleBytes,
    overlapBytes,
  };
}
